import { useState } from "react";
import { editorSections, structureChanged, PLACEHOLDERS } from "../../../../lib/primary/contracts.js";

// Редагування тексту договору: правки поверх шаблону (snap.edits) — змінити, видалити чи повернути пункт, додати пункт
// і розділ. Номери ставляться заново при друку; змінений пункт можна скинути до тексту шаблону
export function ContractTextEditor({ snap, update, edit, sections, title = "Текст договору", note }) {
  const secs = sections || editorSections(snap);
  const [open, setOpen] = useState(null); // ключ пункту, що редагується
  const setEdits = (fn) => update((s) => ({ ...s, edits: fn(structuredClone(s.edits || {})) }));
  const addedKey = (key) => key.match(/^(\d+)\.\+(\d+)$/), extraKey = (key) => key.match(/^\+(\d+)(?:\.(\d+))?$/);
  const setText = (key, text) => setEdits((e) => {
    const a = addedKey(key), x = extraKey(key);
    if (a) (e.added[a[1]][Number(a[2])] = text);
    else if (x) e.sections[Number(x[1])].clauses[Number(x[2])] = text;
    else { e.clauses ||= {}; e.clauses[key] = text; }
    return e;
  });
  const reset = (key) => setEdits((e) => { delete e.clauses?.[key]; return e; });
  const toggleRemove = (key) => setEdits((e) => {
    const a = addedKey(key), x = extraKey(key);
    if (a) e.added[a[1]].splice(Number(a[2]), 1);
    else if (x) e.sections[Number(x[1])].clauses.splice(Number(x[2]), 1);
    else { e.removed ||= {}; if (e.removed[key]) delete e.removed[key]; else e.removed[key] = true; }
    return e;
  });
  const addClause = (sec) => {
    const x = extraKey(sec.key);
    setEdits((e) => {
      if (x) e.sections[Number(x[1])].clauses.push("");
      else { e.added ||= {}; (e.added[sec.key] ||= []).push(""); }
      return e;
    });
    setOpen(x ? `${sec.key}.${sec.clauses.length}` : `${sec.key}.+${sec.clauses.filter((c) => c.added).length}`);
  };
  const setTitle = (sec, title) => setEdits((e) => {
    const x = extraKey(sec.key);
    if (x) e.sections[Number(x[1])].title = title; else { e.titles ||= {}; e.titles[sec.key] = title; }
    return e;
  });
  const toggleSection = (sec) => setEdits((e) => {
    const x = extraKey(sec.key);
    if (x) e.sections.splice(Number(x[1]), 1);
    else { e.removedSections ||= {}; if (e.removedSections[sec.key]) delete e.removedSections[sec.key]; else e.removedSections[sec.key] = true; }
    return e;
  });
  const addSection = () => setEdits((e) => { (e.sections ||= []).push({ title: "Новий розділ", clauses: [""] }); return e; });
  let n = 0;
  return (
    <section className="card" aria-labelledby="ct-text-h">
      <h3 id="ct-text-h">{title}</h3>
      <p className="muted small">Можна змінити будь-який пункт, прибрати його чи додати свій, прибрати цілий розділ. Номери пунктів і розділів ставляться автоматично.
        {note ?? (snap.template !== "custom" ? " Пункти, які ви змінили вручну, не змінюються, коли ви перемикаєте умови вище." : "")}</p>
      {structureChanged(snap) && <p className="notice small" role="note">Структуру змінено — перевірте посилання на номери пунктів у тексті (наприклад, «пункт 4.3»). Посилання на розділ реквізитів оновлюється само.</p>}
      <Placeholders />
      {secs.map((sec) => {
        const live = !sec.removed; if (live) n++;
        let m = 0;
        return (
          <div key={sec.key} className={`ct-sec${sec.removed ? " removed" : ""}`}>
            <div className="ct-sec-head">
              <span className="ct-no">{live ? `${n}.` : "—"}</span>
              {edit && live ? <input className="ct-title" aria-label="Назва розділу" value={sec.title} maxLength={300} onChange={(e) => setTitle(sec, e.target.value)} />
                : <b>{sec.title}</b>}
              {edit && <button type="button" className="btn btn-sm btn-ghost" onClick={() => toggleSection(sec)}>{sec.removed ? "Повернути розділ" : "Прибрати розділ"}</button>}
            </div>
            {live && sec.table && <p className="muted small ct-table">{{ items: "Таблиця позицій", batches: "Таблиця партій", payments: "Таблиця графіка оплати" }[sec.table]} — заповнюється вище</p>}
            {live && sec.clauses.map((c) => {
              const on = !c.removed; if (on) m++;
              const no = on ? `${n}.${m}.` : "—";
              return (
                <div key={c.key} className={`ct-cl${c.removed ? " removed" : ""}${c.edited || c.added ? " changed" : ""}`}>
                  <span className="ct-no">{no}</span>
                  {open === c.key && edit
                    ? <textarea autoFocus rows={Math.min(12, Math.max(3, Math.ceil(c.text.length / 90)))} value={c.text} maxLength={6000}
                      aria-label={`Пункт ${no}`} onChange={(e) => setText(c.key, e.target.value)} />
                    : <p>{c.text || <span className="muted">порожній пункт — не друкується</span>}</p>}
                  {edit && (
                    <span className="ct-acts">
                      {on && <button type="button" className="linkish" onClick={() => setOpen(open === c.key ? null : c.key)}>{open === c.key ? "Готово" : "Змінити"}</button>}
                      {c.edited && <button type="button" className="linkish" onClick={() => reset(c.key)}>Як у шаблоні</button>}
                      <button type="button" className="linkish" onClick={() => toggleRemove(c.key)}>{c.removed ? "Повернути" : "Видалити"}</button>
                      {(c.edited || c.added) && <span className="badge badge-warn">{c.added ? "доданий" : "змінений"}</span>}
                    </span>
                  )}
                </div>
              );
            })}
            {edit && live && <button type="button" className="btn btn-sm btn-ghost" onClick={() => addClause(sec)}>+ Пункт у розділ {n}</button>}
          </div>
        );
      })}
      {edit && <button type="button" className="btn btn-sm btn-secondary" onClick={addSection}>+ Розділ</button>}
    </section>
  );
}

export function Placeholders() {
  return (
    <details className="ct-ph"><summary>Підстановки</summary>
      <p className="muted small">У тексті можна писати підстановки — у договорі на їхньому місці буде значення:</p>
      <ul>{Object.entries(PLACEHOLDERS).map(([k, v]) => <li key={k}><code>{`{{${k}}}`}</code> — {v}</li>)}</ul>
    </details>
  );
}

// Редактор власного шаблону: розділи й пункти напряму (без «шаблону під шаблоном»), з порядком
export function BodyEditor({ body, setBody, edit }) {
  const upd = (fn) => setBody(fn(structuredClone(body)));
  const move = (arr, i, d) => { const j = i + d; if (j < 0 || j >= arr.length) return; [arr[i], arr[j]] = [arr[j], arr[i]]; };
  return (
    <div>
      <Placeholders />
      {body.map((sec, si) => (
        <div key={si} className="ct-sec">
          <div className="ct-sec-head">
            <span className="ct-no">{si + 1}.</span>
            <input className="ct-title" aria-label={`Назва розділу ${si + 1}`} value={sec.title} maxLength={300} disabled={!edit} onChange={(e) => upd((b) => { b[si].title = e.target.value; return b; })} />
            {edit && <span className="ct-acts">
              <button type="button" className="linkish" aria-label={`Розділ ${si + 1} вище`} onClick={() => upd((b) => { move(b, si, -1); return b; })}>↑</button>
              <button type="button" className="linkish" aria-label={`Розділ ${si + 1} нижче`} onClick={() => upd((b) => { move(b, si, 1); return b; })}>↓</button>
              <button type="button" className="linkish" onClick={() => upd((b) => { b.splice(si, 1); return b; })}>Видалити розділ</button></span>}
          </div>
          {sec.clauses.map((text, ci) => (
            <div key={ci} className="ct-cl">
              <span className="ct-no">{si + 1}.{ci + 1}.</span>
              <textarea rows={Math.min(10, Math.max(2, Math.ceil(text.length / 90)))} value={text} maxLength={6000} disabled={!edit} aria-label={`Пункт ${si + 1}.${ci + 1}`}
                onChange={(e) => upd((b) => { b[si].clauses[ci] = e.target.value; return b; })} />
              {edit && <span className="ct-acts">
                <button type="button" className="linkish" aria-label="Вище" onClick={() => upd((b) => { move(b[si].clauses, ci, -1); return b; })}>↑</button>
                <button type="button" className="linkish" aria-label="Нижче" onClick={() => upd((b) => { move(b[si].clauses, ci, 1); return b; })}>↓</button>
                <button type="button" className="linkish" onClick={() => upd((b) => { b[si].clauses.splice(ci, 1); return b; })}>Видалити</button></span>}
            </div>
          ))}
          {edit && <button type="button" className="btn btn-sm btn-ghost" onClick={() => upd((b) => { b[si].clauses.push(""); return b; })}>+ Пункт у розділ {si + 1}</button>}
        </div>
      ))}
      {edit && <button type="button" className="btn btn-sm btn-secondary" onClick={() => upd((b) => [...b, { title: "Новий розділ", clauses: [""] }])}>+ Розділ</button>}
    </div>
  );
}
